import React from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, BellIcon, MessageSquareTextIcon, CalendarDaysIcon, BarChart3Icon, UsersIcon, ZapIcon, StarIcon, InfoIcon, TargetIcon, MessageSquareIcon, SettingsIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { proofIntro, brossardCapabilities, proofMetrics, outcomes, PROOF_REFERENCE, BROSSARD_PANORAMA, type Bi } from '../../data/communicationsProof';
import { CapabilityPreview } from './proof/CapabilityPreviews';
import { CitizenPhone, BrossardDashboard } from './proof/ProofDevices';
const EASE = [0.23, 1, 0.32, 1] as const;
const CAP_ICONS = {
  map: MapPinIcon,
  notices: BellIcon,
  requests: MessageSquareTextIcon,
  collect: CalendarDaysIcon,
  analytics: BarChart3Icon
} as const;
const METRIC_ICONS = {
  registered: UsersIcon,
  speed: ZapIcon,
  since: StarIcon
} as const;
const OUTCOME_ICONS = {
  community: TargetIcon,
  timing: MessageSquareIcon,
  engaged: SettingsIcon,
  decisions: BarChart3Icon
} as const;

// Official Brossard logo, cropped from the supplied reference (≈1774 × 890).
const LOGO_CROP = {
  x: 66,
  y: 112,
  w: 300,
  h: 70,
  W: 1774,
  H: 890
};
function CustomerLogo({
  isFR
}: {
  isFR: boolean;
}) {
  const c = LOGO_CROP;
  return <div data-id="element-2451" data-mp="3gnwa2vj77x1g">
      <div role="img" aria-label={isFR ? 'Logo de la Ville de Brossard' : 'City of Brossard logo'} className="w-[260px] sm:w-[290px]" style={{
      aspectRatio: `${c.w} / ${c.h}`,
      backgroundImage: `url("${PROOF_REFERENCE}")`,
      backgroundSize: `${c.W / c.w * 100}% auto`,
      backgroundPosition: `${c.x / (c.W - c.w) * 100}% ${c.y / (c.H - c.h) * 100}%`,
      backgroundRepeat: 'no-repeat'
    }} data-id="element-2452" data-mp="3gnmfeun7l1pf" />
      <p className="font-sans text-[13px] font-semibold tracking-[0.3em] uppercase text-[#3A4050] mt-2 pl-1" data-id="element-2453" data-mp="3ujhu0oakrqn0">
        {isFR ? proofIntro.client.fr : proofIntro.client.en}
      </p>
    </div>;
}
function CapabilityCard({
  isFR,
  item
}: {
  isFR: boolean;
  item: (typeof brossardCapabilities)[number];
}) {
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  const Icon = CAP_ICONS[item.key];
  return <li className="rounded-2xl border border-[#EEEEF4] bg-white px-4 py-3.5 flex items-center gap-3.5 shadow-[0_12px_28px_-24px_rgba(40,36,120,0.45)]" data-id="element-2454" data-mp="2txk9tbmhnutr">
      <span className="w-11 h-11 rounded-xl bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-2455" data-mp="33vqcvwk17707">
        <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} data-id="element-2456" data-mp="38trwqjpa7acq" />
      </span>
      <span className="min-w-0 flex-1" data-id="element-2457" data-mp="3cvd3dnb82seh">
        <span className="block font-sans text-[15px] font-semibold text-[#121212]" data-id="element-2458" data-mp="3cubp79ywtpsj">{pick(item.title)}</span>
        <span className="block font-sans text-[12.5px] text-[#5B6478] leading-snug mt-0.5" data-id="element-2459" data-mp="3hoy25hkn3qeq">{pick(item.body)}</span>
      </span>
      <span className="w-[118px] h-[64px] shrink-0" data-id="element-2460" data-mp="3rlay6m3nqgcn">
        <CapabilityPreview isFR={isFR} kind={item.key} data-id="element-2461" data-mp="3rmvry9i5dv9g" />
      </span>
    </li>;
}
function ProofMetric({
  isFR,
  item
}: {
  isFR: boolean;
  item: (typeof proofMetrics)[number];
}) {
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  const Icon = METRIC_ICONS[item.key];
  return <div className="h-full rounded-2xl border border-[#EEEEF4] bg-white px-5 py-5 flex items-start gap-4 shadow-[0_16px_36px_-28px_rgba(40,36,120,0.45)]" data-id="element-2462" data-mp="1fwnvr9qbyseb">
      <span className="w-14 h-14 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-2463" data-mp="15zjadc4b70yk">
        <Icon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.6} data-id="element-2464" data-mp="23ntfw13b06uf" />
      </span>
      <span className="min-w-0" data-id="element-2465" data-mp="1yogr3asvgpp7">
        <span className="block font-sans text-[2.5rem] xl:text-[2.8rem] font-semibold text-[#4F46E5] leading-none tracking-tight uppercase whitespace-nowrap" data-id="element-2466" data-mp="1yp8dbv0kxb2t">
          {pick(item.value)}
        </span>
        <span className="block font-sans text-[11.5px] font-semibold tracking-[0.16em] uppercase text-[#121212] mt-3 leading-snug" data-id="element-2467" data-mp="1oxc9s4fq4sr7">{pick(item.label)}</span>
        <span className="block font-sans text-[13.5px] text-[#5B6478] leading-snug mt-1.5" data-id="element-2468" data-mp="2hn1fuw7ajizo">
          {pick(item.body)}
          {item.info && <InfoIcon className="inline-block w-3.5 h-3.5 ml-1.5 -mt-0.5 text-[#8A90A0]" aria-label={isFR ? 'Population approximative' : 'Approximate population'} data-id="element-2469" data-mp="2248kyngzrbst" />}
        </span>
      </span>
    </div>;
}
function CityVisual({
  isFR
}: {
  isFR: boolean;
}) {
  return <div className="relative" data-id="element-2470" data-mp="1x55eqzp9yzj9">
      <div className="relative h-[300px] sm:h-[360px] xl:h-[400px] rounded-2xl xl:rounded-none overflow-hidden" data-id="element-2471" data-mp="1x670by3x34f8">
        <img src={BROSSARD_PANORAMA} alt={isFR ? 'Vue aérienne de Brossard' : 'Aerial view of Brossard'} className="absolute inset-0 w-full h-full object-cover" data-id="element-2472" data-mp="2kxm5rzqzq1up" />
        <div className="hidden xl:block absolute inset-x-0 top-0 h-16 bg-[linear-gradient(to_bottom,#FFFFFF,rgba(255,255,255,0))]" aria-hidden="true" data-id="element-2473" data-mp="2b4oh1bvengnn" />
        <div className="hidden xl:block absolute inset-x-0 bottom-0 h-24 bg-[linear-gradient(to_top,#FFFFFF,rgba(255,255,255,0))]" aria-hidden="true" data-id="element-2474" data-mp="33udljz7bpyu0" />
        <div className="hidden xl:block absolute inset-y-0 left-0 w-20 bg-[linear-gradient(to_right,#FFFFFF,rgba(255,255,255,0))]" aria-hidden="true" data-id="element-2475" data-mp="38lvmin9cjzez" />
        <div className="absolute left-[24%] xl:left-[22%] top-[38%] flex items-center gap-2" data-id="element-2476" data-mp="s71t54e1cev8">
          <MapPinIcon className="w-9 h-9 text-[#4F46E5] fill-[#4F46E5] stroke-white drop-shadow-md" aria-hidden="true" data-id="element-2477" data-mp="16vy69fuy8i2p" />
          <span className="rounded-full bg-white px-4 py-1.5 font-sans text-[15px] font-medium text-[#121212] shadow-[0_8px_20px_-10px_rgba(20,20,60,0.5)]" data-id="element-2478" data-mp="11wbkgi121tia">Brossard</span>
        </div>
      </div>
    </div>;
}
export function CommunicationsProof() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  const body = isFR ? proofIntro.body.fr : proofIntro.body.en;
  return <div className="relative max-w-[1600px] mx-auto" data-id="element-2479" data-mp="j4sgo8z7ibf7">
      <div className="pointer-events-none absolute -right-40 -top-10 w-[480px] h-[480px] rounded-full bg-[#F1F0FD] opacity-70 blur-[120px]" aria-hidden="true" data-id="element-2480" data-mp="1ymu9rpv4dadb" />

      {/* Header: logo left, headline centred */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.3,
      ease: EASE
    }} className="relative grid grid-cols-1 xl:grid-cols-[300px_1fr_300px] gap-8 items-end" data-id="element-2481" data-mp="rbedogltz2ov">
        <div className="order-2 xl:order-1 flex justify-center xl:justify-start" data-id="element-2482" data-mp="15anunihv20tw">
          <CustomerLogo isFR={isFR} data-id="element-2483" data-mp="8kn8lr8vlyga" />
        </div>
        <div className="order-1 xl:order-2 text-center" data-id="element-2484" data-mp="8lycrs68h87x">
          <p className="font-sans text-[12.5px] font-semibold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-2485" data-mp="diezqj1p5gjf">{pick(proofIntro.eyebrow)}</p>
          <h2 className="font-serif text-[2.3rem] sm:text-5xl lg:text-[3.7rem] leading-[1.04] tracking-tight mt-4" data-id="element-2486" data-mp="hj8pzj2m37fh">
            <span className="block text-[#121212]" data-id="element-2487" data-mp="mdmo3t8nrsnu">{pick(proofIntro.line1)}</span>
            <span className="block text-[#4F46E5]" data-id="element-2488" data-mp="3lyi4oh1fcr42">{pick(proofIntro.line2)}</span>
          </h2>
          <p className="font-sans text-[15.5px] md:text-[16.5px] text-[#5B6478] leading-relaxed mt-4" data-id="element-2489" data-mp="3pz25bul7icuy">
            {body.map(l => <span key={l} className="xl:block" data-id="element-2490" data-mp="3dhzpd77hji53">
                {l}{' '}
              </span>)}
          </p>
        </div>
        <div className="hidden xl:block xl:order-3" aria-hidden="true" data-id="element-2491" data-mp="39fct8u3s7b23" />
      </motion.div>

      {/* Body */}
      <motion.div initial={{
      opacity: 0,
      y: 14
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.3,
      delay: 0.06,
      ease: EASE
    }} className="relative mt-8 grid grid-cols-1 xl:grid-cols-[minmax(0,0.3fr)_minmax(0,1fr)] gap-6 xl:gap-8" data-id="element-2492" data-mp="34gjdev10v7yr">
        <div className="order-2 xl:order-1" data-id="element-2493" data-mp="2pqc1yz9fmp7k">
          <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-1 gap-3" aria-label={isFR ? 'Capacités utilisées par Brossard' : 'Capabilities used by Brossard'} data-id="element-2494" data-mp="2lrmlgxt3krjp">
            {brossardCapabilities.map(c => <CapabilityCard key={c.key} isFR={isFR} item={c} data-id="element-2495" data-mp="2btymtwsdezw9" />)}
          </ul>
        </div>

        <div className="order-1 xl:order-2 min-w-0 flex flex-col" data-id="element-2496" data-mp="3kh11tphyl68">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2497" data-mp="8jah9tw64qa3">
            {proofMetrics.map(m => <ProofMetric key={m.key} isFR={isFR} item={m} data-id="element-2498" data-mp="3qnluoevnropi" />)}
          </div>

          {/* City + devices */}
          <div className="relative mt-5 xl:mt-4 xl:flex-1" data-id="element-2499" data-mp="3vm6yeoa4cqip">
            <CityVisual isFR={isFR} data-id="element-2500" data-mp="3gyltcotooh85" />
            <div className="mt-6 xl:mt-0 flex flex-col md:flex-row items-center md:items-end justify-center xl:justify-end gap-5 xl:absolute xl:right-0 xl:bottom-0 xl:top-6" data-id="element-2501" data-mp="3gxan8icrmxdv">
              <div className="xl:relative xl:z-10 xl:-mr-16 xl:mb-[-6px]" data-id="element-2502" data-mp="37z739qhfi8o6">
                <CitizenPhone isFR={isFR} data-id="element-2503" data-mp="3bz96i42tba5k" />
              </div>
              <div className="w-full max-w-[520px] xl:w-[500px] xl:self-start" data-id="element-2504" data-mp="3bxoeom7vu5be">
                <BrossardDashboard isFR={isFR} data-id="element-2505" data-mp="2y0htym3llwjj" />
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Outcomes */}
      <ul className="relative mt-10 pt-8 border-t border-[#EEEEF4] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-0 lg:divide-x divide-[#EEEEF4]" data-id="element-2506" data-mp="2jcrbzuq03ule">
        {outcomes.map(o => {
        const Icon = OUTCOME_ICONS[o.key];
        return <li key={o.key} className="flex items-start gap-4 lg:px-8" data-id="element-2507" data-mp="2t9dvog6lcflu">
              <span className="w-14 h-14 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-2508" data-mp="2obkmde9zr5hc">
                <Icon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.6} data-id="element-2509" data-mp="329si5ivak0qx" />
              </span>
              <span className="min-w-0" data-id="element-2510" data-mp="2xazcgcm8xe1b">
                <span className="block font-sans text-[16.5px] font-semibold text-[#121212]" data-id="element-2511" data-mp="3c27yycuuqja8">{pick(o.title)}</span>
                <span className="block font-sans text-[14px] text-[#5B6478] leading-snug mt-1" data-id="element-2512" data-mp="373wt0d7ogsxa">{pick(o.body)}</span>
              </span>
            </li>;
      })}
      </ul>
    </div>;
}